import React, { useState } from 'react';
import ElectronicPrescriptionPolandAddMedicamentForm, {
	ElectronicPrescriptionPolandFormValues,
} from './ElectronicPrescriptionPolandAddMedicamentForm';
// @ts-ignore
import styles from './ElectronicPrescriptionPoland.module.scss';
import ElectronicPrescriptionPolandMedicationsList from './ElectronicPrescriptionPolandMedicationsList';
import Button from '../../../../Components/Button/Button';
import { Theme } from '../../../../Enums/Theme.enum';
import {
	CreateP1PrescriptionMedicamentsInput,
	useCreateP1PrescriptionMutation,
} from '../../../../graphql/graphql';
import useApiClient from '../../../Base/Hooks/useApiClient';
import useFilters from '../../../Base/Hooks/useFilters';
import useNotification from '../../../../Hooks/useNotification.hook';
import Text from '../../../../Components/Base/Text/Text';
import useP1Patient from '../../../Scope/Hooks/useP1Patient';
import useLoading from '../../../Base/Hooks/useLoading';
import P1InvalidPatientDataMessage from '../../../Scope/Poland/Components/P1InvalidPatientDataMessage/P1InvalidPatientDataMessage';
import useJob from '../../../Base/Hooks/useJob';

interface Props {
	patientId: string;
	onCreate?: () => void;
}

const ElectronicPrescriptionPoland: React.FC<Props> = ({
	patientId,
	onCreate,
}) => {
	const [medications, setMedications] = useState<
		ElectronicPrescriptionPolandFormValues[]
	>([]);
	const { isLoading, setIsLoading, RenderLoading } = useLoading();
	const { displayGQLError } = useApiClient();
	const { handleDateValue } = useFilters();
	const { showNotificationSuccess } = useNotification();
	const { isPatientValid, isLoadingP1Patient } = useP1Patient(patientId);
	const [createPrescription] = useCreateP1PrescriptionMutation();
	const { handleStartWaitingForJob, RenderJobWaiting } = useJob();

	const handleRemoveMedicament = (id: string) => {
		setMedications(prev => prev.filter(item => item.id !== id));
	};

	const handleAddMedicament = (
		values: ElectronicPrescriptionPolandFormValues,
	) => {
		setMedications(prev => {
			return [...prev, values];
		});
	};

	const transformMedications = () => {
		const temp: CreateP1PrescriptionMedicamentsInput[] = [];
		medications.forEach(item => {
			const {
				medicament,
				paymentCoverage,
				validFrom,
				id,
				medicamentChange,
				dosageInterval,
				dosageIntervalCustom,
				recipeRepeat,
				...rest
			} = item;

			const isCustom: boolean = dosageInterval.value === 'custom';

			temp.push({
				...rest,
				id: medicament.value as string,
				paymentCoverage: paymentCoverage.value as string,
				validFrom: handleDateValue(validFrom),
				medicamentChange: !medicamentChange,
				dosageInterval: isCustom
					? dosageIntervalCustom!
					: parseInt(dosageInterval.value as string),
				recipeRepeat: recipeRepeat || 0,
			});
		});
		return temp;
	};

	const handleJobSuccess = () => {
		showNotificationSuccess({ content: 'base.ePrescriptionCreated' });
		onCreate?.();
	};

	const handleIssuePrescription = async () => {
		setIsLoading(true);
		const result = await createPrescription({
			input: {
				patientId: patientId,
				prescriptionMedicaments: transformMedications(),
			},
		});
		displayGQLError(result);
		if ('data' in result) {
			if (result?.data?.createP1Prescription?.jobId) {
				handleStartWaitingForJob(result.data.createP1Prescription.jobId);
			}
		}
		setIsLoading(false);
	};

	if (isLoading || isLoadingP1Patient) {
		return <RenderLoading />;
	}

	if (!isPatientValid) {
		return (
			<div className={styles.wrapper}>
				<P1InvalidPatientDataMessage />
			</div>
		);
	}

	return (
		<>
			<div className={styles.wrapper}>
				<ElectronicPrescriptionPolandAddMedicamentForm
					onSubmit={handleAddMedicament}
				/>
				<ElectronicPrescriptionPolandMedicationsList
					medications={medications}
					onRemove={handleRemoveMedicament}
				/>
				{medications.length > 0 && (
					<div className={styles.controls}>
						<Button
							theme={Theme.GREEN}
							title='component.electronicPrescriptionPoland.issuePrescription'
							onClick={handleIssuePrescription}
						/>
					</div>
				)}
				<RenderJobWaiting onJobSuccess={handleJobSuccess} timeout={15000} />
			</div>
		</>
	);
};

export default ElectronicPrescriptionPoland;
